<script setup lang="ts">
import { computed, ref } from 'vue';
import SidebarLayout from '@/layout/SidebarLayout.vue';
import { config } from '@/config';
import { authFlow } from '@/integrations/Immersve/auth/AuthFlow';

const establishUrl = computed(() => authFlow.establishSessionUrl());
const missingConfigKey = computed(() =>
  config.testModeEnabled ? 'authClientIdTest' : 'authClientIdLive'
);

function establishSession() {
  window.location.href = establishUrl.value;
}

const returnedParams = Object.fromEntries(new URLSearchParams(window.location.search));

const whoamiResult = ref<{ status: number; body: string } | null>(null);
const whoamiError = ref('');
async function checkWhoami() {
  whoamiResult.value = null;
  whoamiError.value = '';
  try {
    const response = await fetch('/api/whoami', { credentials: 'include' });
    const body = await response.text();
    whoamiResult.value = { status: response.status, body };
  } catch (err) {
    whoamiError.value = (err as Error).message;
  }
}
</script>

<template>
  <SidebarLayout>
    <div class="mx-auto max-w-2xl px-6 py-12" data-test="screen-cookie-auth-test">
      <h1 class="mb-2 text-2xl font-bold text-gray-900">Cookie Auth Test</h1>
      <p class="mb-8 text-sm text-gray-500">
        Throwaway diagnostic page for the Cookie Auth Protocol spike — not linked from anywhere in
        the app. Establishes a session against calcite directly, outside larimar's own
        <code>CookieAuthProvider</code> wiring. Both calls below are same-origin — they'll only
        reach a real calcite when this page itself is served from behind calcite (i.e. not your
        local dev server).
      </p>

      <section class="mb-8 rounded-lg border border-gray-200 p-4">
        <h2 class="mb-2 text-sm font-semibold text-gray-900">1. Establish a session</h2>
        <p
          v-if="!authFlow.clientId()"
          data-test="missing-client-id"
          class="mb-3 text-xs text-red-600"
        >
          Set <code>{{ missingConfigKey }}</code> on the
          <RouterLink to="/config" class="underline">/config</RouterLink> page first.
        </p>
        <p class="mb-3 font-mono text-xs break-all text-gray-500">{{ establishUrl }}</p>
        <button
          type="button"
          data-test="btn-establish-session"
          :disabled="!authFlow.clientId()"
          class="rounded-md bg-indigo-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-50"
          @click="establishSession"
        >
          Establish Session
        </button>
      </section>

      <section
        v-if="Object.keys(returnedParams).length > 0"
        class="mb-8 rounded-lg border border-gray-200 p-4"
        data-test="returned-params"
      >
        <h2 class="mb-2 text-sm font-semibold text-gray-900">Query params on this load</h2>
        <p class="mb-2 text-xs text-gray-500">
          Present if you've just landed back here after a round trip through
          <code>/auth/session</code>.
        </p>
        <pre
          class="rounded-md bg-gray-50 p-3 text-xs break-all whitespace-pre-wrap text-gray-800"
          >{{ JSON.stringify(returnedParams, null, 2) }}</pre>
      </section>

      <section class="rounded-lg border border-gray-200 p-4">
        <h2 class="mb-2 text-sm font-semibold text-gray-900">2. Check the session</h2>
        <p class="mb-3 text-xs text-gray-500">
          Calls <code>/api/whoami</code> on this page's own origin. Expected to 401 with no cookie
          attached when run from your local dev server, since the session cookie is scoped to
          wherever it was actually set.
        </p>
        <button
          type="button"
          data-test="btn-check-whoami"
          class="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm font-semibold text-gray-700 hover:bg-gray-50"
          @click="checkWhoami"
        >
          Check /api/whoami
        </button>
        <pre
          v-if="whoamiResult"
          data-test="whoami-result"
          class="mt-3 rounded-md bg-gray-50 p-3 text-xs break-all whitespace-pre-wrap text-gray-800"
          >{{ whoamiResult.status }}
{{ whoamiResult.body }}</pre>
        <p v-if="whoamiError" data-test="whoami-error" class="mt-3 text-xs text-red-600">
          {{ whoamiError }}
        </p>
      </section>
    </div>
  </SidebarLayout>
</template>
